import React, { createContext, useContext, useState, useCallback } from 'react';
import { useServices } from './ServiceContext';
import { useToast } from './ToastContext';
import { Download, X, FileText, Image, Film, Music, Loader2 } from 'lucide-react';

// Tipos de archivos visualizables
const VIEWABLE_TYPES = {
  pdf: ['application/pdf'],
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp'],
  video: ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'],
  audio: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp3'],
};

type ViewerType = 'pdf' | 'image' | 'video' | 'audio';

const isViewable = (mimeType: string): boolean => {
  return Object.values(VIEWABLE_TYPES).flat().includes(mimeType.toLowerCase());
};

const getViewerType = (mimeType: string): ViewerType | null => {
  const mime = mimeType.toLowerCase();
  for (const [type, mimes] of Object.entries(VIEWABLE_TYPES)) {
    if (mimes.includes(mime)) return type as ViewerType;
  }
  return null;
};

const getFileIcon = (viewerType: ViewerType | null) => {
  switch (viewerType) {
    case 'pdf':
      return FileText;
    case 'image':
      return Image;
    case 'video':
      return Film;
    case 'audio':
      return Music;
    default:
      return FileText;
  }
};

interface FileViewerContextType {
  openFile: (fileId: string, fileName: string, mimeType: string) => Promise<void>;
  openFileByUrl: (url: string, fileName: string, mimeType: string) => void;
  downloadFile: (fileId: string, fileName: string) => Promise<void>;
  isLoading: boolean;
}

const FileViewerContext = createContext<FileViewerContextType | null>(null);

export const FileViewerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { fileRepo } = useServices();
  const { success, error: showError } = useToast();

  // Estado del modal
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);

  // Obtener presigned URL del backend
  const getPresignedUrl = useCallback(async (fileId: string): Promise<string> => {
    try {
      const url = await fileRepo.getDownloadUrl(fileId);
      return url;
    } catch (error) {
      console.error('Error getting presigned URL:', error);
      throw new Error('No se pudo obtener la URL del archivo');
    }
  }, [fileRepo]);

  // Abrir archivo (visualizar o descargar según tipo)
  const openFile = useCallback(async (fileId: string, name: string, mime: string) => {
    setIsLoading(true);

    try {
      const url = await getPresignedUrl(fileId);

      if (isViewable(mime)) {
        // Archivo visualizable -> abrir modal
        setFileUrl(url);
        setFileName(name);
        setMimeType(mime);
        setIsOpen(true);
      } else {
        // Archivo no visualizable -> descarga directa
        triggerDownload(url, name);
        success('Descarga iniciada');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al abrir archivo');
    } finally {
      setIsLoading(false);
    }
  }, [getPresignedUrl, success, showError]);

  // Abrir archivo por URL directa (sin llamar al backend)
  const openFileByUrl = useCallback((url: string, name: string, mime: string) => {
    if (isViewable(mime)) {
      setFileUrl(url);
      setFileName(name);
      setMimeType(mime);
      setIsOpen(true);
    } else {
      triggerDownload(url, name);
      success('Descarga iniciada');
    }
  }, [success]);

  // Descargar archivo directamente
  const downloadFile = useCallback(async (fileId: string, name: string) => {
    setIsLoading(true);

    try {
      const url = await getPresignedUrl(fileId);
      triggerDownload(url, name);
      success('Descarga iniciada');
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Error al descargar archivo');
    } finally {
      setIsLoading(false);
    }
  }, [getPresignedUrl, success, showError]);

  // Helper para iniciar descarga
  const triggerDownload = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Cerrar modal
  const closeViewer = useCallback(() => {
    setIsOpen(false);
    setFileUrl(null);
    setFileName(null);
    setMimeType(null);
  }, []);

  // Descargar desde el modal
  const downloadFromModal = useCallback(() => {
    if (fileUrl && fileName) {
      triggerDownload(fileUrl, fileName);
      success('Descarga iniciada');
    }
  }, [fileUrl, fileName, success]);

  const viewerType = mimeType ? getViewerType(mimeType) : null;
  const FileIcon = getFileIcon(viewerType);

  return (
    <FileViewerContext.Provider value={{ openFile, openFileByUrl, downloadFile, isLoading }}>
      {children}

      {/* Modal de visualización */}
      {isOpen && fileUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={(e) => e.target === e.currentTarget && closeViewer()}
        >
          <div
            className="relative w-full max-w-6xl h-[90vh] rounded-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200"
            style={{
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.8)',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white/80">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <FileIcon size={20} className="text-purple-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-800 truncate">{fileName}</h3>
                  <p className="text-xs text-slate-500">{mimeType}</p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={downloadFromModal}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 transition-colors"
                >
                  <Download size={16} />
                  Descargar
                </button>
                <button
                  onClick={closeViewer}
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center p-4">
              {viewerType === 'pdf' && (
                <iframe
                  src={fileUrl}
                  className="w-full h-full rounded-lg border border-slate-200"
                  title={fileName || 'PDF Viewer'}
                />
              )}

              {viewerType === 'image' && (
                <img
                  src={fileUrl}
                  alt={fileName || 'Image'}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                />
              )}

              {viewerType === 'video' && (
                <video
                  src={fileUrl}
                  controls
                  autoPlay={false}
                  className="max-w-full max-h-full rounded-lg shadow-lg"
                >
                  Tu navegador no soporta el elemento de video.
                </video>
              )}

              {viewerType === 'audio' && (
                <div className="flex flex-col items-center gap-6 p-8">
                  <div className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-xl">
                    <Music size={48} className="text-white" />
                  </div>
                  <p className="text-lg font-medium text-slate-700">{fileName}</p>
                  <audio src={fileUrl} controls className="w-full max-w-md">
                    Tu navegador no soporta el elemento de audio.
                  </audio>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay global */}
      {isLoading && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-white/50 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-white shadow-xl">
            <Loader2 size={32} className="text-purple-600 animate-spin" />
            <p className="text-sm text-slate-600">Cargando archivo...</p>
          </div>
        </div>
      )}
    </FileViewerContext.Provider>
  );
};

export const useFileViewer = () => {
  const context = useContext(FileViewerContext);
  if (!context) {
    throw new Error('useFileViewer must be used within a FileViewerProvider');
  }
  return context;
};
